import FooterV2 from 'components/FooterV2/FooterV2'
import HeaderV2 from 'components/HeaderV2/HeaderV2'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { fetchKnowledgeBaseContent } from 'pages/api/knowledgebase'
import { createSlug, reverseSlug } from 'utils/getLocalizedContent'

const SubCategoryPage = ({
  content,
  category,
  subCategory,
  locale,
  breadcrumbs = [],
}) => {
  const { t } = useTranslation('knowledgebase')

  if (!content) return <div>Content not found</div>

  const subCategoryTitle = t('knowledge_base_subcategory_title', {
    subCategory,
    category,
  })
  const subCategoryDescription = t('knowledge_base_subcategory_description', {
    subCategory,
    category,
  })

  return (
    <div className="page-container">
      <HeaderV2 locale={locale} translations={t} />
      <NextSeo
        title={`${category} - ${subCategoryTitle}`}
        description={subCategoryDescription}
        openGraph={{
          title: subCategoryTitle,
          description: subCategoryDescription,
        }}
      />

      <div className="breadcrumbs">
        {breadcrumbs.map((breadcrumb, index) => (
          <span key={index}>
            <Link href={breadcrumb.href}>
              <a>{breadcrumb.label}</a>
            </Link>
            {index < breadcrumbs.length - 1 && ' / '}
          </span>
        ))}
      </div>
      <div className="subcategory-container">
        <h1>{subCategory}</h1>
        <div className="topics">
          {Object.keys(content).map((topic) => (
            <div key={topic} className="topic">
              <Link
                href={`/${locale === 'en' ? 'wbso-knowledge-base' : 'wbso-kennisbank'}/${createSlug(category)}/${createSlug(subCategory)}/${createSlug(topic)}`}
              >
                <a>{topic}</a>
              </Link>
            </div>
          ))}
        </div>
      </div>
      <FooterV2 locale={locale} translations={t} />
      <style jsx>{`
        .page-container {
          display: flex;
          flex-direction: column;
          min-height: 150vh;
          margin-top: 120px;
        }
        .breadcrumbs {
          margin: 20px 0;
          font-size: 14px;
          text-align: center;
        }
        .breadcrumbs a {
          text-decoration: none;
          color: #0070f3;
        }
        .breadcrumbs a:hover {
          text-decoration: underline;
        }
        .subcategory-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          text-align: center;
        }
        .subcategory-container h1 {
          margin-bottom: 20px;
        }
        .topics {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }
        .topic {
          margin: 10px;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          transition: background-color 0.3s;
        }
        .topic:hover {
          background-color: #f0f0f0;
        }
        .topic a {
          text-decoration: none;
          color: #0070f3;
          font-size: 18px;
        }
      `}</style>
    </div>
  )
}

export async function getStaticPaths({ locales }) {
  const paths = []

  // Use Promise.all to fetch the file content for all locales in parallel
  await Promise.all(
    locales.map(async (locale) => {
      const content = await fetchKnowledgeBaseContent(locale)
      // Generate paths for categories and subcategories for each locale
      Object.keys(content).forEach((category) => {
        Object.keys(content[category]).forEach((subCategory) => {
          paths.push({
            params: {
              category: createSlug(category),
              subcategory: createSlug(subCategory),
            },
            locale,
          })
        })
      })
    }),
  )

  return { paths, fallback: false }
}

export async function getStaticProps({ params, locale }) {
  const content = await fetchKnowledgeBaseContent(locale)
  const originalCategory = reverseSlug(params.category, content)
  const originalSubCategory = reverseSlug(
    params.subcategory,
    content[originalCategory],
  )

  const breadcrumbs = [
    {
      label: locale === 'en' ? 'Knowledge Base' : 'Kennisbank',
      href: `/${locale === 'en' ? 'wbso-knowledge-base' : 'wbso-kennisbank'}`,
    },
    {
      label: originalCategory,
      href: `/${locale === 'en' ? 'wbso-knowledge-base' : 'wbso-kennisbank'}/${createSlug(originalCategory)}/`,
    },
    {
      label: originalSubCategory,
      href: `/${locale === 'en' ? 'wbso-knowledge-base' : 'wbso-kennisbank'}/${createSlug(originalCategory)}/${createSlug(originalSubCategory)}/`,
    },
  ]

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'knowledgebase',
        'nav',
        'offer',
      ])),
      content: content[originalCategory][originalSubCategory],
      category: originalCategory,
      subCategory: originalSubCategory,
      breadcrumbs,
      locale,
    },
  }
}

export default SubCategoryPage
